import type { Device } from 'src/types/Device';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { real_id } from 'src/helpers/interface_helpers';
import { isMockLightDevice } from './mock_light_ids';

const LIGHT_STATUS_KEY = /^(light|rgb|rgbw|cct|rgbcct|rgbx2light|cctx2):\d+$/;

type CommandParams = Record<string, unknown>;

const patchLightBlock = (block: Record<string, unknown>, params: CommandParams) => {
	const next = { ...block };
	if (params.on === true || params.turn === 'on' || params.output === true) {
		next.output = true;
		next.ison = true;
	}
	if (params.on === false || params.turn === 'off' || params.output === false) {
		next.output = false;
		next.ison = false;
	}
	if (typeof params.brightness === 'number') next.brightness = params.brightness;
	if (typeof params.gain === 'number') next.gain = params.gain;
	if (Array.isArray(params.rgb)) next.rgb = params.rgb;
	if (typeof params.red === 'number') next.red = params.red;
	if (typeof params.green === 'number') next.green = params.green;
	if (typeof params.blue === 'number') next.blue = params.blue;
	if (typeof params.white === 'number') next.white = params.white;
	if (typeof params.ct === 'number') {
		next.ct = params.ct;
		next.temp = params.ct;
	}
	if (typeof params.temp === 'number') {
		next.temp = params.temp;
		next.ct = params.temp;
	}
	if (
		params.mode === 'rgb' ||
		params.mode === 'cct' ||
		params.mode === 'color' ||
		params.mode === 'white'
	) {
		next.mode = params.mode;
	}
	return next;
};

export const applyMockLightCommand = (
	device: Device,
	methodOrSet: string,
	params: CommandParams = {},
): { isok: true; data: Record<string, never> } => {
	const id = real_id(device.id);
	const current = { ...(devices_status.getByRealId(id) || {}) };
	const method = String(methodOrSet || '').toLowerCase();

	Object.keys(current).forEach((key) => {
		if (!LIGHT_STATUS_KEY.test(key) && key !== 'light:100') return;
		const block = current[key];
		if (!block || typeof block !== 'object') return;
		current[key] = patchLightBlock(block as Record<string, unknown>, params);
	});

	if (Array.isArray(current.lights)) {
		current.lights = (current.lights as Record<string, unknown>[]).map((light) =>
			patchLightBlock(light, params),
		);
	}

	if (
		params.mode === 'color' ||
		params.mode === 'white' ||
		params.mode === 'rgb' ||
		params.mode === 'cct'
	) {
		current.mode = params.mode;
	}

	devices_status.update_device_status({ id, msg: current });

	if (method.includes('setprofile') && typeof params.name === 'string') {
		const settings = { ...(devices_settings.getByRealId(id) || {}) };
		settings.sys = {
			...(settings.sys as Record<string, unknown> | undefined),
			device: {
				...((settings.sys as { device?: Record<string, unknown> } | undefined)?.device || {}),
				profile: params.name,
			},
		};
		devices_settings.update_devices_settings(id, settings);
	} else if (params.mode === 'rgb' || params.mode === 'cct') {
		const settings = { ...(devices_settings.getByRealId(id) || {}) };
		settings['rgbcct:0'] = {
			...((settings['rgbcct:0'] as Record<string, unknown> | undefined) || {}),
			mode: params.mode,
		};
		devices_settings.update_devices_settings(id, settings);
	}

	return { isok: true, data: {} };
};

export const tryApplyMockLightCommand = (
	device: Device | null | undefined,
	methodOrSet: string,
	params: CommandParams = {},
): { isok: true; data: Record<string, never> } | null => {
	if (!isMockLightDevice(device)) return null;
	const nestedParams =
		params?.params && typeof params.params === 'object' && !Array.isArray(params.params)
			? (params.params as CommandParams)
			: params;
	const method = typeof params?.method === 'string' ? params.method : methodOrSet;
	return applyMockLightCommand(device, method, nestedParams);
};
